import { memo, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Account } from "@common/types/domains/accounting/account";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { useAccounts } from "@hooks/useAccounts";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useHasAccrualLedger } from "@api/ledger.api";
import { useUpdateCatalogItem } from "@api/catalogItem.api";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { CatalogItemRow } from "../catalogItems.types";

const ALLOWED_ACCOUNT_TYPES = [AccountType.accountsReceivable];

// Item list's "Receivables account" column — edits the item's
// receivablesAccountId, the default account propagated onto an invoice
// line's receivables coding when this item is selected (see
// LineItemsSection.handleSelectItem). Restricted to accountsReceivable-type
// accounts, matching the invoice line item's Receivables account column.
export const ReceivablesAccountCell = memo(function ReceivablesAccountCell({
    row,
}: CellContext<ViewTableFeatures, CatalogItemRow, unknown>) {
    const item = row.original;
    const updateCatalogItem = useUpdateCatalogItem();
    const showAccountNumbers = useShowAccountNumbers();
    const { accounts } = useAccounts({ entityId: item.entityId });
    // Fails open while the ledger query loads/errors so a persisted receivables
    // account isn't briefly rendered as a blank, non-editable cell.
    const hasAccrualLedger = useHasAccrualLedger(item.entityId);

    const account = accounts.find((a) => a.id === item.receivablesAccountId);

    const handleChange = useCallback(
        async (selectedAccount: Account | null) => {
            await updateCatalogItem.mutateAsync({
                catalogItemId: item.id,
                body: { receivablesAccountId: selectedAccount?.id ?? null },
            });
        },
        [updateCatalogItem, item.id],
    );

    if (item.isNew || !hasAccrualLedger) {
        return <ViewTableCell />;
    }

    return (
        <ViewTableCell captureClicks>
            <AccountCombobox
                showAccountCode={showAccountNumbers}
                selectedAccount={account}
                onChange={handleChange}
                entityId={item.entityId}
                allowedAccountTypes={ALLOWED_ACCOUNT_TYPES}
                canClear
            />
        </ViewTableCell>
    );
});
